/**
 * Generated by orval v7.17.2 🍺
 * Do not edit manually.
 * Radius Proxy API
 * # OpenAPI Specification
This API is documented in [OpenAPI v3.1.0](https://swagger.io/specification/) format.

# Authentication
The API supports two authentication methods:

# Error Responses
Whenever an error appears (whether it's a notifier bug which leads to an internal server error,
or a simple validation error) the API response will be in the
[Problem Details](https://tools.ietf.org/html/rfc7807) format

For example, a 404 response body would look like:
```json
{
    "status": 404,
    "type": "problems/resource-not-found",
    "title": "Resource not found",
    "detail": "Some entity with ID 'sdsada' does not exist."
}
```

# Subscriber Operations

## New Subscriber
Creates authentication data and assigns a speed profile based on USOC-to-group-name mapping.

## Suspend Subscriber
Adds a disable entry for the user with possible values: `non-payment`, `abuse`.

## Remove Suspension
Removes the disable entry to restore service.

## Upgrade/Downgrade Subscriber
Updates the assigned speed profile.

## Manage Static IPs (Idempotent)
Add or remove IP addresses assigned to the subscriber.

## Cancel Subscriber
Complete subscriber cancellation that removes authentication data, speed profile association,
all static IPs, and disable entries.

 * OpenAPI spec version: 1.0
 */
import { useMutation, useQuery } from '@tanstack/react-query';
import type {
  DataTag,
  DefinedInitialDataOptions,
  DefinedUseQueryResult,
  MutationFunction,
  QueryClient,
  QueryFunction,
  QueryKey,
  UndefinedInitialDataOptions,
  UseMutationOptions,
  UseMutationResult,
  UseQueryOptions,
  UseQueryResult,
} from '@tanstack/react-query';

import type {
  BadRequestResponse,
  CreateSubscriberBody,
  InternalServerErrorResponse,
  NotFoundResponse,
  Problem,
  ServiceUnavailableResponse,
  SubscriberResponse,
  SubscriberSuspendedResponse,
  UITokenGenerateToken200,
  UnauthorizedResponse,
  UpsertSubscriberSpeedProfileBody,
  UpsertSubscriberStaticIpAddressesBody,
  UpsertSubscriberSuspensionStatusBody,
} from '.././model';

import { axiosInstance } from '.././axios-instance';

type SecondParameter<T extends (...args: never) => unknown> = Parameters<T>[1];

/**
 * Creates a new subscriber with authentication data and assigns a speed profile.
Same functionality as the API endpoint but uses JWT Bearer authentication.

 * @summary Create a Subscriber
 */
export const uISubscribersCreateSubscriber = (
  createSubscriberBody: CreateSubscriberBody,
  options?: SecondParameter<typeof axiosInstance>,
  signal?: AbortSignal,
) => {
  return axiosInstance<SubscriberResponse>(
    {
      url: `/ui/subscribers`,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      data: createSubscriberBody,
      signal,
    },
    options,
  );
};

export const getUISubscribersCreateSubscriberMutationOptions = <
  TError = BadRequestResponse | UnauthorizedResponse | InternalServerErrorResponse,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof uISubscribersCreateSubscriber>>,
    TError,
    { data: CreateSubscriberBody },
    TContext
  >;
  request?: SecondParameter<typeof axiosInstance>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof uISubscribersCreateSubscriber>>,
  TError,
  { data: CreateSubscriberBody },
  TContext
> => {
  const mutationKey = ['uISubscribersCreateSubscriber'];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof uISubscribersCreateSubscriber>>,
    { data: CreateSubscriberBody }
  > = (props) => {
    const { data } = props ?? {};

    return uISubscribersCreateSubscriber(data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type UISubscribersCreateSubscriberMutationResult = NonNullable<
  Awaited<ReturnType<typeof uISubscribersCreateSubscriber>>
>;
export type UISubscribersCreateSubscriberMutationBody = CreateSubscriberBody;
export type UISubscribersCreateSubscriberMutationError =
  | BadRequestResponse
  | UnauthorizedResponse
  | InternalServerErrorResponse;

/**
 * @summary Create a Subscriber
 */
export const useUISubscribersCreateSubscriber = <
  TError = BadRequestResponse | UnauthorizedResponse | InternalServerErrorResponse,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof uISubscribersCreateSubscriber>>,
      TError,
      { data: CreateSubscriberBody },
      TContext
    >;
    request?: SecondParameter<typeof axiosInstance>;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof uISubscribersCreateSubscriber>>,
  TError,
  { data: CreateSubscriberBody },
  TContext
> => {
  const mutationOptions = getUISubscribersCreateSubscriberMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};
/**
 * Retrieves subscriber information including speed profile, suspension status, assigned IP addresses, and active BNG session data.
 * @summary Get a Subscriber
 */
export const uISubscribersGetSubscriber = (
  username: string,
  options?: SecondParameter<typeof axiosInstance>,
  signal?: AbortSignal,
) => {
  return axiosInstance<SubscriberResponse>(
    { url: `/ui/subscribers/${username}`, method: 'GET', signal },
    options,
  );
};

export const getUISubscribersGetSubscriberQueryKey = (username?: string) => {
  return [`/ui/subscribers/${username}`] as const;
};

export const getUISubscribersGetSubscriberQueryOptions = <
  TData = Awaited<ReturnType<typeof uISubscribersGetSubscriber>>,
  TError = UnauthorizedResponse | NotFoundResponse | InternalServerErrorResponse,
>(
  username: string,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof uISubscribersGetSubscriber>>, TError, TData>>;
    request?: SecondParameter<typeof axiosInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getUISubscribersGetSubscriberQueryKey(username);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof uISubscribersGetSubscriber>>> = ({ signal }) =>
    uISubscribersGetSubscriber(username, requestOptions, signal);

  return { queryKey, queryFn, enabled: !!username, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof uISubscribersGetSubscriber>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type UISubscribersGetSubscriberQueryResult = NonNullable<
  Awaited<ReturnType<typeof uISubscribersGetSubscriber>>
>;
export type UISubscribersGetSubscriberQueryError =
  | UnauthorizedResponse
  | NotFoundResponse
  | InternalServerErrorResponse;

export function useUISubscribersGetSubscriber<
  TData = Awaited<ReturnType<typeof uISubscribersGetSubscriber>>,
  TError = UnauthorizedResponse | NotFoundResponse | InternalServerErrorResponse,
>(
  username: string,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof uISubscribersGetSubscriber>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof uISubscribersGetSubscriber>>,
          TError,
          Awaited<ReturnType<typeof uISubscribersGetSubscriber>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof axiosInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useUISubscribersGetSubscriber<
  TData = Awaited<ReturnType<typeof uISubscribersGetSubscriber>>,
  TError = UnauthorizedResponse | NotFoundResponse | InternalServerErrorResponse,
>(
  username: string,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof uISubscribersGetSubscriber>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof uISubscribersGetSubscriber>>,
          TError,
          Awaited<ReturnType<typeof uISubscribersGetSubscriber>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof axiosInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useUISubscribersGetSubscriber<
  TData = Awaited<ReturnType<typeof uISubscribersGetSubscriber>>,
  TError = UnauthorizedResponse | NotFoundResponse | InternalServerErrorResponse,
>(
  username: string,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof uISubscribersGetSubscriber>>, TError, TData>>;
    request?: SecondParameter<typeof axiosInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Get a Subscriber
 */

export function useUISubscribersGetSubscriber<
  TData = Awaited<ReturnType<typeof uISubscribersGetSubscriber>>,
  TError = UnauthorizedResponse | NotFoundResponse | InternalServerErrorResponse,
>(
  username: string,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof uISubscribersGetSubscriber>>, TError, TData>>;
    request?: SecondParameter<typeof axiosInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getUISubscribersGetSubscriberQueryOptions(username, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * Deletes a subscriber and performs all necessary cleanup operations:
- Removes authentication data
- Clears speed-profile associations
- Deletes any assigned static IPs
- Removes any existing disable-entry data

 * @summary Delete a Subscriber
 */
export const uISubscribersDeleteSubscriber = (
  username: string,
  options?: SecondParameter<typeof axiosInstance>,
) => {
  return axiosInstance<void>({ url: `/ui/subscribers/${username}`, method: 'DELETE' }, options);
};

export const getUISubscribersDeleteSubscriberMutationOptions = <
  TError = UnauthorizedResponse | InternalServerErrorResponse | ServiceUnavailableResponse,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof uISubscribersDeleteSubscriber>>,
    TError,
    { username: string },
    TContext
  >;
  request?: SecondParameter<typeof axiosInstance>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof uISubscribersDeleteSubscriber>>,
  TError,
  { username: string },
  TContext
> => {
  const mutationKey = ['uISubscribersDeleteSubscriber'];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof uISubscribersDeleteSubscriber>>,
    { username: string }
  > = (props) => {
    const { username } = props ?? {};

    return uISubscribersDeleteSubscriber(username, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type UISubscribersDeleteSubscriberMutationResult = NonNullable<
  Awaited<ReturnType<typeof uISubscribersDeleteSubscriber>>
>;

export type UISubscribersDeleteSubscriberMutationError =
  | UnauthorizedResponse
  | InternalServerErrorResponse
  | ServiceUnavailableResponse;

/**
 * @summary Delete a Subscriber
 */
export const useUISubscribersDeleteSubscriber = <
  TError = UnauthorizedResponse | InternalServerErrorResponse | ServiceUnavailableResponse,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof uISubscribersDeleteSubscriber>>,
      TError,
      { username: string },
      TContext
    >;
    request?: SecondParameter<typeof axiosInstance>;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof uISubscribersDeleteSubscriber>>,
  TError,
  { username: string },
  TContext
> => {
  const mutationOptions = getUISubscribersDeleteSubscriberMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};
/**
 * Ensures that the list of static IPs is assigned to the subscriber.
This operation is **idempotent**.

 * @summary Upsert Subscriber Static IP Addresses
 */
export const uISubscribersUpsertSubscriberStaticIpAddresses = (
  username: string,
  upsertSubscriberStaticIpAddressesBody: UpsertSubscriberStaticIpAddressesBody,
  options?: SecondParameter<typeof axiosInstance>,
) => {
  return axiosInstance<SubscriberResponse>(
    {
      url: `/ui/subscribers/${username}/static-ip-addresses`,
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      data: upsertSubscriberStaticIpAddressesBody,
    },
    options,
  );
};

export const getUISubscribersUpsertSubscriberStaticIpAddressesMutationOptions = <
  TError =
    | BadRequestResponse
    | UnauthorizedResponse
    | SubscriberSuspendedResponse
    | InternalServerErrorResponse
    | ServiceUnavailableResponse,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof uISubscribersUpsertSubscriberStaticIpAddresses>>,
    TError,
    { username: string; data: UpsertSubscriberStaticIpAddressesBody },
    TContext
  >;
  request?: SecondParameter<typeof axiosInstance>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof uISubscribersUpsertSubscriberStaticIpAddresses>>,
  TError,
  { username: string; data: UpsertSubscriberStaticIpAddressesBody },
  TContext
> => {
  const mutationKey = ['uISubscribersUpsertSubscriberStaticIpAddresses'];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof uISubscribersUpsertSubscriberStaticIpAddresses>>,
    { username: string; data: UpsertSubscriberStaticIpAddressesBody }
  > = (props) => {
    const { username, data } = props ?? {};

    return uISubscribersUpsertSubscriberStaticIpAddresses(username, data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type UISubscribersUpsertSubscriberStaticIpAddressesMutationResult = NonNullable<
  Awaited<ReturnType<typeof uISubscribersUpsertSubscriberStaticIpAddresses>>
>;
export type UISubscribersUpsertSubscriberStaticIpAddressesMutationBody =
  UpsertSubscriberStaticIpAddressesBody;
export type UISubscribersUpsertSubscriberStaticIpAddressesMutationError =
  | BadRequestResponse
  | UnauthorizedResponse
  | SubscriberSuspendedResponse
  | InternalServerErrorResponse
  | ServiceUnavailableResponse;

/**
 * @summary Upsert Subscriber Static IP Addresses
 */
export const useUISubscribersUpsertSubscriberStaticIpAddresses = <
  TError =
    | BadRequestResponse
    | UnauthorizedResponse
    | SubscriberSuspendedResponse
    | InternalServerErrorResponse
    | ServiceUnavailableResponse,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof uISubscribersUpsertSubscriberStaticIpAddresses>>,
      TError,
      { username: string; data: UpsertSubscriberStaticIpAddressesBody },
      TContext
    >;
    request?: SecondParameter<typeof axiosInstance>;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof uISubscribersUpsertSubscriberStaticIpAddresses>>,
  TError,
  { username: string; data: UpsertSubscriberStaticIpAddressesBody },
  TContext
> => {
  const mutationOptions = getUISubscribersUpsertSubscriberStaticIpAddressesMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};
/**
 * Ensures assignment of the speed profile to the subscriber. Used for upgrades and downgrades.
 * @summary Upsert Subscriber Speed Profile
 */
export const uISubscribersUpsertSubscriberSpeedProfile = (
  username: string,
  upsertSubscriberSpeedProfileBody: UpsertSubscriberSpeedProfileBody,
  options?: SecondParameter<typeof axiosInstance>,
) => {
  return axiosInstance<SubscriberResponse>(
    {
      url: `/ui/subscribers/${username}/speed-profile`,
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      data: upsertSubscriberSpeedProfileBody,
    },
    options,
  );
};

export const getUISubscribersUpsertSubscriberSpeedProfileMutationOptions = <
  TError =
    | BadRequestResponse
    | UnauthorizedResponse
    | SubscriberSuspendedResponse
    | InternalServerErrorResponse
    | ServiceUnavailableResponse,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof uISubscribersUpsertSubscriberSpeedProfile>>,
    TError,
    { username: string; data: UpsertSubscriberSpeedProfileBody },
    TContext
  >;
  request?: SecondParameter<typeof axiosInstance>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof uISubscribersUpsertSubscriberSpeedProfile>>,
  TError,
  { username: string; data: UpsertSubscriberSpeedProfileBody },
  TContext
> => {
  const mutationKey = ['uISubscribersUpsertSubscriberSpeedProfile'];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof uISubscribersUpsertSubscriberSpeedProfile>>,
    { username: string; data: UpsertSubscriberSpeedProfileBody }
  > = (props) => {
    const { username, data } = props ?? {};

    return uISubscribersUpsertSubscriberSpeedProfile(username, data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type UISubscribersUpsertSubscriberSpeedProfileMutationResult = NonNullable<
  Awaited<ReturnType<typeof uISubscribersUpsertSubscriberSpeedProfile>>
>;
export type UISubscribersUpsertSubscriberSpeedProfileMutationBody = UpsertSubscriberSpeedProfileBody;
export type UISubscribersUpsertSubscriberSpeedProfileMutationError =
  | BadRequestResponse
  | UnauthorizedResponse
  | SubscriberSuspendedResponse
  | InternalServerErrorResponse
  | ServiceUnavailableResponse;

/**
 * @summary Upsert Subscriber Speed Profile
 */
export const useUISubscribersUpsertSubscriberSpeedProfile = <
  TError =
    | BadRequestResponse
    | UnauthorizedResponse
    | SubscriberSuspendedResponse
    | InternalServerErrorResponse
    | ServiceUnavailableResponse,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof uISubscribersUpsertSubscriberSpeedProfile>>,
      TError,
      { username: string; data: UpsertSubscriberSpeedProfileBody },
      TContext
    >;
    request?: SecondParameter<typeof axiosInstance>;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof uISubscribersUpsertSubscriberSpeedProfile>>,
  TError,
  { username: string; data: UpsertSubscriberSpeedProfileBody },
  TContext
> => {
  const mutationOptions = getUISubscribersUpsertSubscriberSpeedProfileMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};
/**
 * Ensures the subscriber’s suspension status, allowing both suspension and resumption.
 * @summary Upsert Subscriber Suspension Status
 */
export const uISubscribersUpsertSubscriberSuspensionStatus = (
  username: string,
  upsertSubscriberSuspensionStatusBody: UpsertSubscriberSuspensionStatusBody,
  options?: SecondParameter<typeof axiosInstance>,
) => {
  return axiosInstance<SubscriberResponse>(
    {
      url: `/ui/subscribers/${username}/suspension`,
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      data: upsertSubscriberSuspensionStatusBody,
    },
    options,
  );
};

export const getUISubscribersUpsertSubscriberSuspensionStatusMutationOptions = <
  TError =
    | BadRequestResponse
    | UnauthorizedResponse
    | InternalServerErrorResponse
    | ServiceUnavailableResponse,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof uISubscribersUpsertSubscriberSuspensionStatus>>,
    TError,
    { username: string; data: UpsertSubscriberSuspensionStatusBody },
    TContext
  >;
  request?: SecondParameter<typeof axiosInstance>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof uISubscribersUpsertSubscriberSuspensionStatus>>,
  TError,
  { username: string; data: UpsertSubscriberSuspensionStatusBody },
  TContext
> => {
  const mutationKey = ['uISubscribersUpsertSubscriberSuspensionStatus'];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof uISubscribersUpsertSubscriberSuspensionStatus>>,
    { username: string; data: UpsertSubscriberSuspensionStatusBody }
  > = (props) => {
    const { username, data } = props ?? {};

    return uISubscribersUpsertSubscriberSuspensionStatus(username, data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type UISubscribersUpsertSubscriberSuspensionStatusMutationResult = NonNullable<
  Awaited<ReturnType<typeof uISubscribersUpsertSubscriberSuspensionStatus>>
>;
export type UISubscribersUpsertSubscriberSuspensionStatusMutationBody = UpsertSubscriberSuspensionStatusBody;
export type UISubscribersUpsertSubscriberSuspensionStatusMutationError =
  | BadRequestResponse
  | UnauthorizedResponse
  | InternalServerErrorResponse
  | ServiceUnavailableResponse;

/**
 * @summary Upsert Subscriber Suspension Status
 */
export const useUISubscribersUpsertSubscriberSuspensionStatus = <
  TError =
    | BadRequestResponse
    | UnauthorizedResponse
    | InternalServerErrorResponse
    | ServiceUnavailableResponse,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof uISubscribersUpsertSubscriberSuspensionStatus>>,
      TError,
      { username: string; data: UpsertSubscriberSuspensionStatusBody },
      TContext
    >;
    request?: SecondParameter<typeof axiosInstance>;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof uISubscribersUpsertSubscriberSuspensionStatus>>,
  TError,
  { username: string; data: UpsertSubscriberSuspensionStatusBody },
  TContext
> => {
  const mutationOptions = getUISubscribersUpsertSubscriberSuspensionStatusMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};
/**
 * Generates a JWT token for development and testing purposes.

**Available only in dev/local environments.** Returns 404 in production.

The generated token uses fixed credentials (`employees` realm, user ID `25`)
and can be used to authenticate requests to `/ui/` endpoints during development.

 * @summary Generate Development Token
 */
export const uITokenGenerateToken = (
  options?: SecondParameter<typeof axiosInstance>,
  signal?: AbortSignal,
) => {
  return axiosInstance<UITokenGenerateToken200>({ url: `/ui/token`, method: 'GET', signal }, options);
};

export const getUITokenGenerateTokenQueryKey = () => {
  return [`/ui/token`] as const;
};

export const getUITokenGenerateTokenQueryOptions = <
  TData = Awaited<ReturnType<typeof uITokenGenerateToken>>,
  TError = Problem,
>(options?: {
  query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof uITokenGenerateToken>>, TError, TData>>;
  request?: SecondParameter<typeof axiosInstance>;
}) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getUITokenGenerateTokenQueryKey();

  const queryFn: QueryFunction<Awaited<ReturnType<typeof uITokenGenerateToken>>> = ({ signal }) =>
    uITokenGenerateToken(requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof uITokenGenerateToken>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type UITokenGenerateTokenQueryResult = NonNullable<Awaited<ReturnType<typeof uITokenGenerateToken>>>;
export type UITokenGenerateTokenQueryError = Problem;

export function useUITokenGenerateToken<
  TData = Awaited<ReturnType<typeof uITokenGenerateToken>>,
  TError = Problem,
>(
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof uITokenGenerateToken>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof uITokenGenerateToken>>,
          TError,
          Awaited<ReturnType<typeof uITokenGenerateToken>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof axiosInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useUITokenGenerateToken<
  TData = Awaited<ReturnType<typeof uITokenGenerateToken>>,
  TError = Problem,
>(
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof uITokenGenerateToken>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof uITokenGenerateToken>>,
          TError,
          Awaited<ReturnType<typeof uITokenGenerateToken>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof axiosInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useUITokenGenerateToken<
  TData = Awaited<ReturnType<typeof uITokenGenerateToken>>,
  TError = Problem,
>(
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof uITokenGenerateToken>>, TError, TData>>;
    request?: SecondParameter<typeof axiosInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Generate Development Token
 */

export function useUITokenGenerateToken<
  TData = Awaited<ReturnType<typeof uITokenGenerateToken>>,
  TError = Problem,
>(
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof uITokenGenerateToken>>, TError, TData>>;
    request?: SecondParameter<typeof axiosInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getUITokenGenerateTokenQueryOptions(options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
}
